import type { User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

type NavigatorWithExtras = Navigator & {
  deviceMemory?: number
  connection?: { effectiveType?: string; type?: string }
  vendor?: string
}

/**
 * Capture every browser/device signal we can without prompting the user, then
 * insert a row into public.sign_in_events. The DB trigger fills in the IP.
 *
 * Best-effort: if any access throws, we still try to insert the rest.
 */
export async function recordSignIn(user: User | null) {
  if (!user) return
  try {
    const nav = (typeof navigator !== 'undefined' ? navigator : null) as NavigatorWithExtras | null
    const win = typeof window !== 'undefined' ? window : null
    const screenObj = typeof screen !== 'undefined' ? screen : null
    const mql = (q: string) => (win?.matchMedia ? win.matchMedia(q).matches : null)

    const colorScheme = mql('(prefers-color-scheme: dark)')
      ? 'dark'
      : mql('(prefers-color-scheme: light)')
        ? 'light'
        : null

    const reducedMotion = mql('(prefers-reduced-motion: reduce)')

    const tz = (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null
      } catch {
        return null
      }
    })()

    const payload = {
      user_id: user.id,
      email: user.email ?? '',
      user_agent: nav?.userAgent ?? null,
      languages: nav?.languages ? Array.from(nav.languages) : nav?.language ? [nav.language] : null,
      timezone: tz,
      screen_width: screenObj?.width ?? null,
      screen_height: screenObj?.height ?? null,
      viewport_width: win?.innerWidth ?? null,
      viewport_height: win?.innerHeight ?? null,
      device_pixel_ratio: win?.devicePixelRatio ?? null,
      color_scheme: colorScheme,
      reduced_motion: reducedMotion,
      platform: nav?.platform ?? null,
      vendor: nav?.vendor ?? null,
      hardware_concurrency: nav?.hardwareConcurrency ?? null,
      device_memory: nav?.deviceMemory ?? null,
      connection_type: nav?.connection?.effectiveType ?? nav?.connection?.type ?? null,
      online: nav?.onLine ?? null,
      referrer: typeof document !== 'undefined' ? document.referrer || null : null,
      page_url: win?.location?.href ?? null,
    }

    const { error } = await supabase.from('sign_in_events').insert(payload)
    if (error) {
      // Don't block the user on telemetry failure — just log to console.
      console.warn('[vice] sign-in telemetry failed:', error.message)
    }
  } catch (err) {
    console.warn('[vice] sign-in telemetry threw:', err)
  }
}
